import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { createDefaultStore } from "./store";
import { createOutbox } from "./outbox";
import { readCache, refreshCache } from "./cache";

const OfflineContext = createContext(null);
export const useOffline = () => useContext(OfflineContext);

const DEVICE_KEY = "phoenix.device_code";
function deviceCode() {
  let c = localStorage.getItem(DEVICE_KEY);
  if (!c) {
    c = "D" + Math.random().toString(36).slice(2, 5).toUpperCase();
    localStorage.setItem(DEVICE_KEY, c);
  }
  return c;
}

/** Replays one queued action to the server using its id as Idempotency-Key. */
export const sendItem = (item) => {
  if (item.kind !== "create_document") throw new Error(`Unknown outbox kind ${item.kind}`);
  return api.post("/documents/", item.payload, { headers: { "Idempotency-Key": item.id } });
};

export function OfflineProvider({ children, store: injected, send = sendItem, interval = 30000 }) {
  const store = useMemo(() => injected || createDefaultStore(), [injected]);
  const outbox = useMemo(() => createOutbox(store, { send, deviceCode: deviceCode() }), [store, send]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [counts, setCounts] = useState({ pending: 0, attention: 0 });
  const [syncing, setSyncing] = useState(false);
  const [cache, setCache] = useState({ parties: [], products: [], warehouses: [], refreshedAt: null });
  const alive = useRef(true);

  const refreshCounts = useCallback(async () => {
    const c = await outbox.counts();
    if (alive.current) setCounts(c);
  }, [outbox]);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      await outbox.flush();
    } finally {
      if (alive.current) setSyncing(false);
      await refreshCounts();
    }
  }, [outbox, refreshCounts]);

  const refreshMasters = useCallback(async () => {
    try { setCache(await refreshCache(api, store)); } catch { /* keep the last copy */ }
  }, [store]);

  useEffect(() => {
    alive.current = true;
    readCache(store).then((c) => alive.current && setCache(c));
    refreshCounts();
    const up = () => { setOnline(true); sync().then(refreshMasters); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    const t = setInterval(() => { if (navigator.onLine) sync(); }, interval);
    if (navigator.onLine) refreshMasters();
    return () => {
      alive.current = false;
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      clearInterval(t);
    };
  }, [store, sync, refreshCounts, refreshMasters, interval]);

  const enqueue = useCallback(async (kind, payload) => {
    const item = await outbox.enqueue(kind, payload);
    await refreshCounts();
    if (navigator.onLine) sync();
    return item;
  }, [outbox, refreshCounts, sync]);

  const value = { store, online, syncing, counts, cache, outbox, enqueue, sync, refreshMasters, refreshCounts };
  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}
